import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { imgSrc } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function Gallery({ items }) {
  const [filter, setFilter] = useState("All");
  const [openIdx, setOpenIdx] = useState(null);

  const close = useCallback(() => setOpenIdx(null), []);
  const cats = ["All", ...new Set((items || []).map((g) => g.category).filter(Boolean))];
  const filtered = filter === "All" ? (items || []) : (items || []).filter((g) => g.category === filter);
  const len = filtered.length;
  useEffect(() => {
    if (openIdx === null) return;
    const fn = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setOpenIdx((i) => (i + 1) % len);
      if (e.key === "ArrowLeft") setOpenIdx((i) => (i - 1 + len) % len);
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [openIdx, len, close]);

  if (!items?.length) return null;

  return (
    <section id="gallery" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="gallery-section">
      <Reveal>
        <p className="eyebrow mb-4">Gallery</p>
        <h2 className="max-w-3xl font-serif text-2xl leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
          Frames from the <span className="italic text-copper-gradient">golden hour.</span>
        </h2>
      </Reveal>
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Gallery filters">
        {cats.map((c) => (
          <button key={c} role="tab" aria-selected={filter === c} onClick={() => setFilter(c)}
            data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-[0.15em] transition-colors duration-300 ${filter === c ? "border-copper bg-copper text-charcoal" : "border-white/15 text-cream-muted hover:border-copper/50 hover:text-copper-300"}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
        {filtered.map((g, i) => (
          <Reveal key={g.id || i} delay={0.04 * (i % 6)}>
            <button
              onClick={() => { setOpenIdx(i); track("gallery_open", { image: g.alt }); }}
              data-testid={`gallery-item-${g.id || i}`}
              className="group relative block w-full overflow-hidden rounded-xl border border-white/10"
              aria-label={`Open ${g.alt}`}
            >
              <img src={imgSrc(g.url)} alt={g.alt} loading="lazy"
                className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <span className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute bottom-3 left-4 text-xs font-light text-cream opacity-0 transition-opacity duration-500 group-hover:opacity-100">{g.alt}</span>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {openIdx !== null && filtered[openIdx] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-charcoal/95 backdrop-blur-xl"
            role="dialog" aria-modal="true" aria-label="Image viewer" data-testid="gallery-lightbox"
            onClick={close}>
            <button className="absolute right-5 top-5 text-cream" onClick={close} data-testid="lightbox-close-btn" aria-label="Close viewer"><X className="h-7 w-7" /></button>
            <button className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 p-3 text-cream transition-colors hover:border-copper" data-testid="lightbox-prev-btn" aria-label="Previous image"
              onClick={(e) => { e.stopPropagation(); setOpenIdx((i) => (i - 1 + filtered.length) % filtered.length); }}>
              <ChevronLeft className="h-6 w-6" />
            </button>
            <motion.img
              key={filtered[openIdx].id || openIdx}
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              src={imgSrc(filtered[openIdx].url)} alt={filtered[openIdx].alt}
              className="max-h-[82vh] max-w-[88vw] rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/15 p-3 text-cream transition-colors hover:border-copper" data-testid="lightbox-next-btn" aria-label="Next image"
              onClick={(e) => { e.stopPropagation(); setOpenIdx((i) => (i + 1) % filtered.length); }}>
              <ChevronRight className="h-6 w-6" />
            </button>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm font-light text-cream-muted">{filtered[openIdx].alt}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
